import React, { useState } from 'react';
import { useMedia } from 'react-use';
import { LazyLoadImage, Loading, Typography } from 'ck-ui';
import { getImgixUrl } from 'ck-utils';
import { useWishlist } from 'ck-utils/hooks/useWishlist';
import { algoliaClient, INDEX_PRODUCTS } from 'src/lib/algolia';
import { ProductListingPageProvider } from '../ProductListingPage/context';
import { WishlistCollection } from './WishlistCollection';
import styles from './WishListPage.module.scss';

const PRODUCTS_PER_PAGE = 12;
const WishListPage = () => {
  const [showWishlistCollection, setShowWishlistCollection] = useState(false);
  const [currentList, setCurrentList] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [ProductHits, setProductHits] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useMedia('(max-width: 767px)');
  const { wishListCollections } = useWishlist((list) => {
    setLoading(false);
    if (!currentList?.lid) return;
    setCurrentList(list.filter(l => l.lid === currentList.lid)[0]);
  });

  const triggerUpdate = () => {
    window.dispatchEvent(new CustomEvent('getLatestWishLists'));
  };

  /**
   * Deletes a product from passed wishlist, swat.deleteFromList doesnt trigger a js event hence why we have too
   * @param {Object} wishlist item object {lid, id, handle,variantId}
   */
  const deleteProductFromWishList = ({ lid, variantId, handle, id }) => {
    window.theme.useSwat((swat) => {
      swat.deleteFromList(lid, { epi: variantId, du: handle, empi: id }, () => {
        setProductHits(ProductHits.filter((item) => `${item.id}` !== id));
        setCurrentList({
          ...currentList,
          products: [
            ...currentList.products.filter((item) => item.id !== id)
          ]
        });
        setCurrentIndex(currentIndex - 1);
        triggerUpdate();
      });
    });
  };

  /**
   * get product hits From Algolia and delete obsolete products
   * @param {Object} list - the wishlist list
   * @param startIndex - start index for load more
   * @returns product hits
   */
  const getAlgoliaProductData = async (list, startIndex) => {
    setLoading(true);
    const queryProducts = list.products.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
    const filters = queryProducts.reduce((acc, curr, index) => {
      const objectID = `objectID:"gid://shopify/Product/${curr.id}"`;
      if (index === 0) {
        acc += objectID;
      } else {
        acc += ` OR ${objectID}`;
      }
      return acc;
    }, '');

    const currentProductionIndex = algoliaClient.initIndex(INDEX_PRODUCTS);
    const { hits, queryID } = await currentProductionIndex.search('', {
      clickAnalytics: true,
      analyticsTags: [
        'web',
        'wishlist'
      ],
      filters });

    const obsoletedProducts = [];
    if (hits.length < queryProducts.length) {
      queryProducts.forEach((item) => {
        if (hits.findIndex((hit) => `${hit.id}` === item.id) === -1) {
          const { id, variantId, handle } = item;
          obsoletedProducts.push({ empi: id, epi: variantId, du: handle });
        }
      });
    }

    if (obsoletedProducts.length > 0) {
      window.theme.useSwat((swat) => {
        swat.removeProductsFromList(list.lid, obsoletedProducts, () => {
          setLoading(false);
          setCurrentIndex(startIndex + PRODUCTS_PER_PAGE - obsoletedProducts.length);
          triggerUpdate();
        });
      });
    } else {
      setLoading(false);
      setCurrentIndex(startIndex + PRODUCTS_PER_PAGE);
    }

    return hits.map((hit, index) => ({
      ...hit,
      __queryID: queryID,
      __position: index + 1,
    }));
  };

  // Gets the wishlist which the user clicks on
  const goToWishlistCollection = async (list) => {
    if (list.products.length === 0) {
      setProductHits([]);
    } else {
      setProductHits(await getAlgoliaProductData(list, 0));
    }
    setCurrentList(list);
    setShowWishlistCollection(true);
  };

  /**
   * Delete the whole wishlist
   * @param {Object} lid - The wishlist id, swat.deleteList doesnt trigger a js event so we do
   */
  const deleteWishList = (lid) => {
    setLoading(true);
    window.theme.useSwat((swat) => swat.deleteList(lid, () => triggerUpdate()));
  };

  const loadMore = async () => {
    setProductHits([...ProductHits, ...await getAlgoliaProductData(currentList, currentIndex)]);
  };

  if (loading && !showWishlistCollection) {
    return <Loading isLoading />;
  }

  if (!showWishlistCollection) {
    return (
      <div className={styles.root}>
        {!wishListCollections?.length
          ? <Typography variant="heading5" align="centered">This Wishlist is empty!!!</Typography>
          : wishListCollections?.map((item) => (
            <a key={item.lid} tabIndex={0} className={styles.card} role="button" onClick={() => goToWishlistCollection(item)}>
              <Typography variant="heading5">{item.lname}</Typography>
              <Typography display="inline" variant="body">
                {`${item.products.length} Products`}
              </Typography>
              <div className={styles.imgWrapper}>
                {item.products.length ? (
                  item.products.slice(0, isMobile ? 2 : 3).map((product) => (
                    <LazyLoadImage
                      key={product.id}
                      src={`${getImgixUrl(product.image, [
                        'fit=crop',
                        'w=300'
                      ])}`}
                      className={styles.img}
                      alt={product.title}
                    />
                  ))
                ) : (<Typography align="centered">This Wishlist is empty!!!</Typography>)}
              </div>
              <Typography variant="heading6" align="centered">
                View list
              </Typography>
            </a>
          ))}
      </div>
    );
  }

  return (
    <ProductListingPageProvider>
      <WishlistCollection
        goBack={() => setShowWishlistCollection(false)}
        currentList={currentList}
        ProductHits={ProductHits}
        indexName={INDEX_PRODUCTS}
        deleteProductFromWishList={deleteProductFromWishList}
        deleteWishList={deleteWishList}
        loadMore={loadMore}
        loading={loading}
      />
    </ProductListingPageProvider>
  );
};

export { WishListPage };
